// =============================
// Imports
// =============================

import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { Field, Form } from 'react-final-form';

import { Desktop, Mobile } from '../../../other/responsive';
import MewoInput from '../../../presentationals/mewoInput';

import validator from '../../../../validators/auth/register';

import { getValueByLocale } from '../../../../helpers/i18n';
import * as pth from '../../../../helpers/proptypes';

import BAM from '../bam';

import {
  AgreeToTOS,
  AuthTitle,
  ButtonLink,
  ButtonWrapper,
  Container,
  DoubleInputWrapper,
  InputWrapper,
  LinksWrapper,
  Notification,
  StyledButton,
  StyledCheckboxInput,
  StyledForm,
  StyledInput,
  StyledLink,
  StyledSelectInput,
  StyledUserLanguages,
  Wrapper,
} from '../common.styles';

// =============================
// Stories
// =============================

class Register extends Component {
  static propTypes = {
    config: pth.config.isRequired,
    /** If True, has access to website when private. */
    hasAccess: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    registerUser: PropTypes.func.isRequired,
    setLegalOpen: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    territories: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
    ).isRequired,
    userIndustries: PropTypes.arrayOf(pth.userIndustry).isRequired,
    newsletterActive: PropTypes.bool.isRequired,
  };

  render() {
    const {
      config,
      hasAccess,
      locale,
      registerUser,
      setLegalOpen,
      t,
      territories,
      userIndustries,
      newsletterActive,
    } = this.props;

    const countryOptions = territories.map(({ code, name }) => ({ label: name, value: code }));

    return (
      <Wrapper>
        <Fragment>
          <Container>
            <Form
              onSubmit={registerUser}
              initialValues={{
                ...(newsletterActive ? {
                  withNewsletter: false,
                } : {}),
              }}
            >
              {({ values, submitError, handleSubmit, submitting }) => (
                <StyledForm onSubmit={handleSubmit}>
                  <AuthTitle>{t('pages:register.title')}</AuthTitle>
                  {submitError && <Notification type="error">{submitError}</Notification>}
                  <InputWrapper>
                    <DoubleInputWrapper>
                      <Field
                        name="lastName"
                        component={MewoInput}
                        use={StyledInput}
                        type="text"
                        placeholder={t('common:form.last_name')}
                        validate={validator.lastName}
                      />
                      <Field
                        name="firstName"
                        component={MewoInput}
                        use={StyledInput}
                        type="text"
                        placeholder={t('common:form.first_name')}
                        validate={validator.firstName}
                      />
                    </DoubleInputWrapper>
                    <DoubleInputWrapper>
                      <Field
                        name="email"
                        component={MewoInput}
                        use={StyledInput}
                        type="text"
                        placeholder={t('common:form.email')}
                        validate={validator.email}
                      />
                    </DoubleInputWrapper>
                    <DoubleInputWrapper>
                      <Field
                        name="phoneNumber"
                        component={MewoInput}
                        use={StyledInput}
                        type="text"
                        placeholder={t('common:form.phone_number')}
                        validate={validator.phoneNumber}
                      />
                      <Field
                        name="companyName"
                        component={MewoInput}
                        use={StyledInput}
                        type="text"
                        placeholder={t('common:form.company_name')}
                      />
                    </DoubleInputWrapper>
                    <DoubleInputWrapper>
                      <Field
                        name="industry"
                        component={MewoInput}
                        use={StyledSelectInput}
                        type="text"
                        placeholder={t('common:form.industry')}
                        options={userIndustries.map(({ id, names }) => ({
                          value: id,
                          label: getValueByLocale(names, locale),
                        }))}
                        selectAllHelper={false}
                      />
                      <Field
                        name="country"
                        component={MewoInput}
                        use={StyledSelectInput}
                        type="text"
                        placeholder={t('common:form.country')}
                        options={countryOptions}
                        selectAllHelper={false}
                        searchable
                      />
                    </DoubleInputWrapper>
                    <DoubleInputWrapper>
                      <Field
                        name="password"
                        component={MewoInput}
                        use={StyledInput}
                        type="password"
                        placeholder={t('common:form.password')}
                        validate={validator.password}
                      />
                      <Field
                        name="passwordConfirmation"
                        component={MewoInput}
                        use={StyledInput}
                        type="password"
                        placeholder={t('common:form.password_confirmation')}
                        validate={validator.passwordConfirmation(
                          values.password,
                          t('common:form.password'),
                        )}
                      />
                    </DoubleInputWrapper>
                  </InputWrapper>
                  {newsletterActive && (
                    <InputWrapper>
                      <Field
                        name="withNewsletter"
                        component={MewoInput}
                        use={StyledCheckboxInput}
                        type="checkbox"
                        label={t('common:form.newsletter')}
                      />
                    </InputWrapper>
                  )}
                  <LinksWrapper>
                    <AgreeToTOS>
                      {t('pages:register.tos_you_agree_to')}
                      &nbsp;
                      {hasAccess ? (
                        <StyledLink inline route="/legal">
                          {t('pages:register.terms_of_service')}
                        </StyledLink>
                      ) : (
                        <StyledLink inline onClick={() => setLegalOpen(true)}>
                          {t('pages:register.terms_of_service')}
                        </StyledLink>
                      )}
                    </AgreeToTOS>
                  </LinksWrapper>
                  <ButtonWrapper>
                    <Mobile>
                      <StyledLink mt="2rem" route="/login">{t('pages:register.backto_signin')}</StyledLink>
                    </Mobile>
                    <Desktop>
                      <ButtonLink route="/login">
                        <StyledButton fillVariant="outline">{t('pages:register.backto_signin')}</StyledButton>
                      </ButtonLink>
                    </Desktop>
                    <StyledButton disabled={submitting} firstOnMobile type="submit">
                      {t('pages:register.register')}
                    </StyledButton>
                  </ButtonWrapper>
                </StyledForm>
              )}
            </Form>

            <Mobile>
              <StyledUserLanguages opened />
            </Mobile>

            <BAM config={config} register />

          </Container>

          <Desktop>
            <StyledUserLanguages opened />
          </Desktop>
        </Fragment>
      </Wrapper>
    );
  }
}

export default Register;
